import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { timer } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

// The fs_testing cookie enables FullStory in non-prod environments for instrumentation
// validation. Its value is the BRANCH_NAME baked in at build time, so the active state
// is scoped to the branch it was enabled on:
//
//   - Switching to a different branch: hasFsTestingCookie() returns false (branch mismatch),
//     but the cookie is kept so FS resumes automatically when switching back.
//   - Explicitly disabling via the backstage toggle: the cookie is deleted entirely, so FS
//     does not resume when switching back to that branch.
//   - Cookie expiry (24h): same as explicit disable — gone, no auto-resume.
//   - Local dev (BRANCH_NAME not injected): branch validation is skipped, cookie value is
//     'true', and FS testing works as it did before this feature.
@Injectable({
  providedIn: 'root',
})
export class FullstoryTestingCookieService {
  public isTestingFullStory$ = timer(0, 1000).pipe(
    map(() => this.hasFsTestingCookie()),
    distinctUntilChanged()
  );

  constructor(private cookieService: CookieService) {}

  public hasFsTestingCookie(): boolean {
    if (!this.cookieService.check('fs_testing')) {
      return false;
    }
    const currentBranch: string | null = process.env.BRANCH_NAME;
    if (!currentBranch) {
      // Local dev: BRANCH_NAME not injected at build time, preserve existing behavior
      return true;
    }
    const cookieValue = this.cookieService.get('fs_testing');
    return cookieValue === currentBranch;
  }

  // Add cookie that will expire in 24hrs, scoped to the current branch
  public setFsTestingCookie() {
    this.cookieService.set(
      'fs_testing',
      process.env.BRANCH_NAME || 'true',
      new Date(new Date().getTime() + 24 * 60 * 60 * 1000),
      '/'
    );
  }

  public deleteFsTestingCookie() {
    this.cookieService.delete('fs_testing', '/');
  }
}
